import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ENVIRONMENT } from '@env/environment.model';
import { SKIP_CONNECTION_LOST_NOTICE, SKIP_GLOBAL_LOADING } from '@app/core/http/http-context.tokens';
import {
  RfcChallenge,
  TokenStatus,
  TokenStatusResult,
} from '@app/core/session/onboarding-session.model';

// DTO de `GET /api/v1/public/onboarding-landing/{token}/status`. Solo esta capa lo conoce.
interface TokenStatusDto {
  success: boolean;
  data?: {
    numero?: number;
    status: string;
    expiresAt: string | null;
    ttlHours: number;
    canWrite: boolean;
    rfcChallenge?: RfcChallengeDto | null;
  };
}

interface RfcChallengeDto {
  prefix: string;
  missingLength: number;
  attemptsRemaining: number;
}

// Decisión de negocio: con `success`, `pending`, `in_progress` y `consulted` van al RFC y `completed` a completado;
// cualquier otra respuesta es vencido. `in_progress` = el cliente ya entró y no terminó (ej. F5 a mitad del flujo), no
// que venció. `consulted` = ya validó el RFC antes, pero el sufijo solo vive en memoria: lo tiene que volver a ingresar.
const STATUS_BY_DTO: Readonly<Partial<Record<string, TokenStatus>>> = {
  pending: 'in-progress',
  in_progress: 'in-progress',
  consulted: 'in-progress',
  completed: 'completed',
};

const EXPIRED: TokenStatusResult = { status: 'expired', rfcChallenge: null };

/** Consulta el estado del token. El backend lo recibe en la URL (contrato del endpoint público). */
@Injectable({ providedIn: 'root' })
export class TokenStatusApi {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(ENVIRONMENT).apiUrl}/api/v1/public/onboarding-landing`;

  /** Emite el estado mapeado. Los 4xx/5xx y errores de red salen como error del observable. */
  getStatus(token: string): Observable<TokenStatusResult> {
    return this.http.get<TokenStatusDto | null>(this.statusUrl(token)).pipe(map(toTokenStatusResult));
  }

  /**
   * Estado del token para el polling de la sesión ya validada. A diferencia de `getStatus`, no exige el desafío del
   * RFC, y una respuesta inesperada es `null` (se registra), no vencido: a mitad del flujo solo sacan al cliente los
   * estados explícitos. Corre en segundo plano: sin overlay global (dejaría la página `inert` cada minuto) y sin el
   * aviso de conexión (el poller reintenta solo). Los 4xx/5xx y errores de red salen como error del observable.
   */
  pollStatus(token: string): Observable<TokenStatus | null> {
    return this.http
      .get<TokenStatusDto | null>(this.statusUrl(token), {
        context: new HttpContext().set(SKIP_GLOBAL_LOADING, true).set(SKIP_CONNECTION_LOST_NOTICE, true),
      })
      .pipe(map(toPolledStatus));
  }

  private statusUrl(token: string): string {
    return `${this.baseUrl}/${encodeURIComponent(token)}/status`;
  }
}

function toPolledStatus(dto: TokenStatusDto | null): TokenStatus | null {
  const success = dto?.success;
  const status = dto?.data?.status;
  // hasOwn: un status como "toString" no tiene que encontrar nada en el prototipo.
  if (success === true && typeof status === 'string' && Object.hasOwn(STATUS_BY_DTO, status)) {
    return STATUS_BY_DTO[status] ?? null;
  }
  // Para detectar cambios del backend. Solo `success` y `status`: el desafío trae el prefijo del RFC.
  console.warn('Unexpected onboarding token status while polling', { success, status });
  return null;
}

function toTokenStatusResult(dto: TokenStatusDto | null): TokenStatusResult {
  const data = dto?.success === true ? dto.data : undefined;
  const expiresAt = data?.expiresAt;
  // Solo se muestran: si faltan o vienen raros se omiten, pero el estado vale igual.
  return {
    ...toStatusAndChallenge(data),
    ...(isValidId(data?.numero) && { requestNumber: String(data?.numero) }),
    ...(typeof expiresAt === 'string' && expiresAt !== '' && { expiresAt }),
  };
}

function toStatusAndChallenge(data: TokenStatusDto['data']): TokenStatusResult {
  const status = data?.status;
  // hasOwn: un status como "toString" no tiene que encontrar nada en el prototipo.
  if (typeof status !== 'string' || !Object.hasOwn(STATUS_BY_DTO, status)) {
    return EXPIRED;
  }
  const mapped = STATUS_BY_DTO[status] ?? 'expired';
  if (mapped !== 'in-progress') {
    return { status: mapped, rfcChallenge: null };
  }
  // Sin un desafío válido no hay cómo pedir el RFC: es una respuesta inesperada, igual que un status desconocido.
  const rfcChallenge = toRfcChallenge(data?.rfcChallenge);
  return rfcChallenge ? { status: mapped, rfcChallenge } : EXPIRED;
}

function toRfcChallenge(dto: RfcChallengeDto | null | undefined): RfcChallenge | null {
  if (
    typeof dto?.prefix !== 'string' ||
    !Number.isInteger(dto.missingLength) ||
    dto.missingLength < 1 ||
    !Number.isInteger(dto.attemptsRemaining) ||
    dto.attemptsRemaining < 0
  ) {
    return null;
  }
  return {
    prefix: dto.prefix,
    missingLength: dto.missingLength,
    attemptsRemaining: dto.attemptsRemaining,
  };
}

const isValidId = (value: unknown): boolean => Number.isFinite(value) || (typeof value === 'string' && value !== '');
